import { Button, showDialog } from '@bpinternal/ui-kit-next'
import { capitalize } from 'lodash'
import { BotIcon, EqualIcon, PlugIcon, PlusIcon } from 'lucide-react'
import plusPlanBg from '~/assets/plus_bg.jpg'
import teamPlanBg from '~/assets/team_bg.jpg'
import linearLogo from '~/assets/logo/linear.png'
import { HelpDeskButton } from '~/components/navigation/HeaderSettings'
import { cn } from '~/utils'

export const showWelcomeDialog = ({ planName, workspaceId }: Omit<WelcomeDialogProps, 'close'>) => {
  return showDialog(({ close }) => <WelcomeDialog planName={planName} close={close} workspaceId={workspaceId} />, {
    size: 'max-w-2xl',
    layout: 'content-only',
  })
}

type WelcomeDialogProps = { planName: 'plus' | 'team'; close?: () => void; workspaceId: string }

const PLAN_CONTENT: Record<
  WelcomeDialogProps['planName'],
  { description: string; showQuestions: boolean; backgroundUrl: string; backgroundColor: string }
> = {
  plus: {
    description: 'Build and ship AI agents fast; with a simple, efficient, and feature-packed experience.',
    showQuestions: false,
    backgroundUrl: plusPlanBg,
    backgroundColor: 'bg-blue-400',
  },
  team: {
    description: 'Your Customer Success Manager will be in touch soon to schedule onboarding.',
    showQuestions: true,
    backgroundUrl: teamPlanBg,
    backgroundColor: 'bg-green-400',
  },
}

export const WelcomeDialog = ({ planName, close, workspaceId }: WelcomeDialogProps) => {
  return (
    <div className="flex flex-row items-center">
      <div className="flex flex-col gap-10 px-8 py-20 sm:w-[55%] sm:flex-shrink-0">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-semibold text-foreground">
            Welcome to <br />
            Botpress {capitalize(planName)}! 🎉
          </h1>
          <p className="text-sm text-foreground">{PLAN_CONTENT[planName].description}</p>
          {PLAN_CONTENT[planName].showQuestions && (
            <p className="text-sm text-muted-foreground">
              Questions?{' '}
              <HelpDeskButton
                workspaceId={workspaceId}
                variant="link"
                className="p-0 text-sm font-normal text-muted-foreground underline"
              >
                Access live chat support
              </HelpDeskButton>
            </p>
          )}
        </div>

        <Button className="w-fit" onClick={close}>
          Start Building
        </Button>
      </div>
      <div
        className={cn(
          'hidden h-full w-full flex-col items-center justify-center rounded-r-lg sm:flex',
          PLAN_CONTENT[planName].backgroundColor
        )}
        style={{ backgroundImage: `url(${PLAN_CONTENT[planName].backgroundUrl})`, backgroundSize: 'cover' }}
      >
        <WorkflowCard />
      </div>
    </div>
  )
}

const WorkflowCard = () => {
  return (
    <div
      className="relative flex w-[206px] flex-col gap-2 rounded-lg border border-white/50 bg-white/50 p-2.5 text-neutral-500 backdrop-blur-lg"
      style={{ fontSize: '10px', lineHeight: '14px' }}
    >
      <div className="flex flex-row items-center gap-1 text-white">
        <EqualIcon className="h-3.5 w-3.5" />
        Human Handoff
      </div>
      <div className="flex flex-row items-center gap-1 rounded-lg border border-white/50 bg-white/50 p-1.5">
        <img src={linearLogo} className="h-3.5 w-3.5" />
        Report Escalation
      </div>
      <div className="flex flex-row items-center gap-1 rounded-lg border border-white/50 bg-white/50 p-1.5">
        <PlugIcon className="h-3.5 w-3.5 text-amber-500" />
        Start HITL
      </div>
      <div className="flex flex-row items-center gap-1 rounded-lg border border-dashed border-white/50 p-1.5 text-white">
        <PlusIcon className="h-3.5 w-3.5" />
        Add Card
      </div>
      <div className="absolute -bottom-6 -right-3 left-8 flex -rotate-[8deg] flex-row items-center gap-1 rounded-lg border border-white/50 bg-white/50 p-1.5 backdrop-blur-2xl">
        <BotIcon className="h-3.5 w-3.5" />
        Track Event
      </div>
    </div>
  )
}
